import React, { useState, useEffect, useRef } from "react";
import { useSafeWindowManager } from "@/hooks/useSafeWindowManager";
import { useWidgetManager } from "@/contexts/WidgetManager";
import { useWeather } from "@/contexts/WeatherContext";
import { usePreferences } from "@/contexts/PreferencesContext";
import { Plus } from "lucide-react";
import { AnimatedWeatherIcon } from "./animated-weather-icon";
import NotificationDropdown from "./notification-dropdown-with-panel";
import TaskCreatorWindow from "@/components/windows/TaskCreatorWindow";
import { FocusModeMenuBarIndicator } from "@/components/notifications/FocusModeMenuBarIndicator";
import { useTaskManager } from "@/contexts/TaskManager";
import Lottie from "lottie-react";
import fingerprintAnimation from "@/assets/fingerprint-auth.json";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarShortcut,
  MenubarTrigger,
} from "@/components/ui/menubar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logoWhite from "@/assets/open-insurance-logo-white.png";
import { defaultSystemMenus, taskCreatorMenuConfig, notesMenuConfig } from "@/lib/AppMenuRegistry";
import { MenuActionHandler } from "@/lib/MenuActionHandler";
import { MenuAction } from "@/types/MenuTypes";
import { DynamicMenu } from "@/components/ui/dynamic-menu";
import { useAuthStatus } from "@/hooks/useAuthStatus";
import { AuthStatusDialog } from "@/components/ui/auth-status-dialog";
import { AboutDialog } from "@/components/ui/about-dialog";
import { PinChangeMenuHandler } from "@/components/pin/PinChangeMenuHandler";
import { useUser, OrganizationSwitcher, useOrganizationList } from "@clerk/clerk-react";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface OSMenuBarProps {
  kpis?: any;
}

function OSMenuBarContent({ kpis }: OSMenuBarProps) {
  const { windowManager } = useSafeWindowManager();
  const { openWindow, closeWindow, activeWindow } = windowManager;
  const { openWidget } = useWidgetManager();
  const { addTask } = useTaskManager();
  const { weatherData, config } = useWeather();
  const { timeMode } = usePreferences();
  const { user } = useUser();
  const { currentOrganization, organizations, loading, switchOrganization } = useOrganizationContext();
  const { organizationList, userMemberships } = useOrganizationList() as any;
  const navigate = useNavigate();
  
  // Create ref for the OrganizationSwitcher
  const orgSwitcherRef = useRef<HTMLDivElement>(null);
  
  // Initialize menu action handler
  const menuActionHandler = new MenuActionHandler(
    { openWindow, closeWindow },
    { addTask },
    null, // notes manager if available
    undefined,
    () => {
      // Handle sign out through Clerk
      import('@clerk/clerk-react').then(({ useClerk }) => {
        // This is a workaround - in a real implementation we'd need proper Clerk context
        window.location.href = '/login';
      });
    }
  );

  // Get current menu configuration based on active window
  const currentMenuConfig = activeWindow?.menuConfig || defaultSystemMenus;
  
  // Time-based color system with preferences support
  const [isNightTime, setIsNightTime] = useState(false);
  
  useEffect(() => {
    const checkTimeOfDay = () => {
      if (timeMode === 'day') {
        setIsNightTime(false);
      } else if (timeMode === 'night') {
        setIsNightTime(true);
      } else { // auto mode
        const hour = new Date().getHours();
        // Night time: 7 PM (19:00) to 6 AM (06:00)
        setIsNightTime(hour >= 19 || hour < 6);
      }
    };
    
    checkTimeOfDay();
    // Check every minute for auto mode
    const interval = setInterval(checkTimeOfDay, 60000);
    
    return () => clearInterval(interval);
  }, [timeMode]);
  
  // Dynamic gradient with dramatic flowing animation
  const getMenuBarStyle = () => {
    if (isNightTime) {
      return {
        background: 'linear-gradient(90deg, hsl(200, 100%, 50%) 0%, hsl(280, 100%, 35%) 12%, hsl(199, 100%, 15%) 25%, hsl(220, 100%, 45%) 37%, hsl(199, 100%, 10%) 50%, hsl(200, 100%, 40%) 62%, hsl(280, 100%, 30%) 75%, hsl(199, 100%, 20%) 87%, hsl(200, 100%, 50%) 100%)',
        backgroundSize: '300% 100%',
        animation: 'gradientFlow 6s linear infinite, nightGlow 4s ease-in-out infinite'
      };
    } else {
      return {
        background: 'linear-gradient(90deg, hsl(13, 100%, 55%) 0%, hsl(30, 100%, 55%) 12%, hsl(13, 100%, 45%) 25%, hsl(25, 100%, 65%) 37%, hsl(13, 100%, 40%) 50%, hsl(35, 100%, 60%) 62%, hsl(13, 100%, 50%) 75%, hsl(20, 100%, 70%) 87%, hsl(13, 100%, 55%) 100%)',
        backgroundSize: '300% 100%',
        animation: 'gradientFlow 6s linear infinite, orangeGlow 4s ease-in-out infinite'
      };
    }
  };


  // Dynamic dropdown styling with rainbow support
  const getDropdownStyle = () => {
    if (timeMode === 'rainbow') {
      // Return empty inline styles, use className instead
      return {
        background: undefined,
        borderColor: 'hsla(280, 100%, 70%, 0.3)',
        animation: undefined
      };
    } else if (isNightTime) {
      return {
        background: 'linear-gradient(135deg, hsl(210, 70%, 15%) 0%, hsl(220, 80%, 8%) 50%, hsl(200, 70%, 20%) 100%)',
        borderColor: 'hsl(210, 50%, 30%)',
        backgroundSize: '200% 100%',
        animation: 'gradientFlow 12s ease-in-out infinite'
      };
    } else {
      return {
        background: 'linear-gradient(135deg, hsl(13, 80%, 35%) 0%, hsl(13, 90%, 45%) 100%)',
        borderColor: 'hsl(13, 60%, 50%)',
        backgroundSize: '200% 100%',
        animation: 'gradientFlow 12s ease-in-out infinite'
      };
    }
  };

  // Get dropdown className (adds rainbow class when needed)
  const getDropdownClassName = (baseClassName: string) => {
    if (timeMode === 'rainbow') {
      return `${baseClassName} dropdown-rainbow-gradient`;
    }
    return baseClassName;
  };
  
  // Weather display component
  const WeatherDisplay = () => {
    const { weatherData, config } = useWeather();
    
    if (!weatherData) {
      return <span>--°</span>;
    }
    
    const convertTemperature = (temp: number, unit: 'celsius' | 'fahrenheit') => {
      if (unit === 'fahrenheit') {
        return Math.round((temp * 9/5) + 32);
      }
      return temp;
    };
    
    const displayTemp = convertTemperature(weatherData.temperature, config.temperatureUnit);
    const tempUnit = config.temperatureUnit === 'celsius' ? '°C' : '°F';
    
    return <span>{displayTemp}{tempUnit}</span>;
  };

  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [isAboutDialogOpen, setIsAboutDialogOpen] = useState(false);
  const [orgSwitchDialog, setOrgSwitchDialog] = useState<{
    isOpen: boolean;
    targetOrg: { id: string; name: string } | null;
  }>({ isOpen: false, targetOrg: null });

  // Listen for about dialog event
  useEffect(() => {
    const handleShowAboutDialog = () => setIsAboutDialogOpen(true);
    window.addEventListener('showAboutDialog', handleShowAboutDialog);
    return () => window.removeEventListener('showAboutDialog', handleShowAboutDialog);
  }, []);

  const AuthStatusIndicator = () => {
    const authStatus = useAuthStatus();
    
    return (
      <>
        <button 
          onClick={() => setIsAuthDialogOpen(true)}
          className="flex items-center gap-1 hover:bg-white/10 rounded-md px-2 py-1 transition-colors duration-200"
        >
          <div className="relative w-6 h-6 flex items-center justify-center">
            {authStatus.status === 'authenticated' ? (
              <Lottie 
                animationData={fingerprintAnimation}
                loop={false}
                autoplay={false}
                className="w-6 h-6"
              />
            ) : (
              <>
                {/* Outer pulse ring */}
                <div className={`absolute inset-0 w-2 h-2 ${authStatus.dotColor}/30 rounded-full animate-ping`} />
                {/* Core status dot */}
                <div className={`relative w-1 h-1 ${authStatus.dotColor} rounded-full`} />
              </>
            )}
          </div>
          <span className="text-xs text-white/80 font-medium">{authStatus.label}</span>
        </button>
        
        <AuthStatusDialog 
          open={isAuthDialogOpen} 
          onOpenChange={setIsAuthDialogOpen} 
        />
      </>
    );
  };
  
  
  const [currentTime, setCurrentTime] = useState("");
  const [currentDate, setCurrentDate] = useState("");

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      }));
      
      // Only update date when it actually changes (not every second)
      const newDate = now.toLocaleDateString([], {
        weekday: 'short',
        month: 'short', 
        day: 'numeric'
      });
      setCurrentDate(prev => prev !== newDate ? newDate : prev);
    };

    updateTime(); // Set initial time
    // Update every MINUTE instead of every second to reduce re-renders
    const interval = setInterval(updateTime, 60000);

    return () => clearInterval(interval);
  }, []);

  // Enhanced menu action handlers for new/existing functionality
  const handleNewWindow = (type: string) => {
    switch (type) {
      case 'task':
        openWindow(
          'task-creator', 
          'Create New Task', 
          <TaskCreatorWindow onClose={() => closeWindow('task-creator')} />,
          { 
            appType: 'task-creator',
            menuConfig: taskCreatorMenuConfig
          }
        );
        break;
      case 'note':
        // We'll need to import and use NotesWindow here once implemented
        openWindow(
          'notes', 
          'Notes', 
          <div>Notes Window Placeholder</div>,
          { 
            appType: 'notes',
            menuConfig: notesMenuConfig
          }
        );
        break;
      default:
        console.log(`Window type ${type} not supported`);
    }
  };

  // Determine which gradient class to use
  const getMenuBarGradientClass = () => {
    if (timeMode === 'rainbow') {
      return 'menu-bar-rainbow-gradient';
    } else if (timeMode === 'night') {
      return 'menu-bar-night-gradient';
    } else if (timeMode === 'day') {
      return 'menu-bar-day-gradient';
    } else { // auto mode
      return isNightTime ? 'menu-bar-night-gradient' : 'menu-bar-day-gradient';
    }
  };


  const handleConfirmOrgSwitch = async () => {
    if (orgSwitchDialog.targetOrg) {
      await switchOrganization(orgSwitchDialog.targetOrg.id);
    }
    setOrgSwitchDialog({ isOpen: false, targetOrg: null });
  };

  return (
    <>
      <AboutDialog open={isAboutDialogOpen} onOpenChange={setIsAboutDialogOpen} />
      <PinChangeMenuHandler />
      <PinChangeMenuHandler />
      
      {/* Organization Switch Confirmation Dialog */}
      <AlertDialog open={orgSwitchDialog.isOpen} onOpenChange={(open) => {
        if (!open) setOrgSwitchDialog({ isOpen: false, targetOrg: null });
      }}>
        <AlertDialogContent className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700/50 backdrop-blur-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white text-xl">Switch Organization?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              You are about to switch from <span className="font-semibold text-white">{currentOrganization?.name}</span> to{' '}
              <span className="font-semibold text-white">{orgSwitchDialog.targetOrg?.name}</span>.
              <br /><br />
              This will change your active workspace and reload your apps and settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 hover:bg-slate-600 text-white border-slate-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmOrgSwitch}
              className="bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 text-white"
            >
              Switch Organization
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <div className={`fixed top-0 left-0 right-0 h-7 px-4 flex items-center backdrop-blur-xl border-b border-white/10 overflow-hidden ${getMenuBarGradientClass()}`}
           style={{ zIndex: 10000 }}>
        {/* Apple-style Clickable Logo */}
        <div className="flex items-center gap-4 mr-4">
        <DropdownMenu onOpenChange={(open) => {
          if (open) {
            // Refetch organizations when dropdown opens to ensure fresh data
            console.log('[MenuBar] Dropdown opened, current orgs:', organizations?.map(o => ({ id: o.id, name: o.name, clerk_org_id: o.clerk_org_id, slug: o.slug })));
            console.log('[MenuBar] Current organization:', currentOrganization);
            console.log('[MenuBar] Total orgs:', organizations?.length);
            console.log('[MenuBar] Clerk memberships:', userMemberships?.data?.map((m: any) => ({ id: m?.organization?.id, name: m?.organization?.name })));
            console.log('[MenuBar] userMemberships full:', userMemberships);
            
            // Test the filter logic - TEMPORARILY show all orgs with valid clerk_org_id
            const filtered = organizations?.filter(org => {
              if (!org || !org.id || !org.name) return false;
              
              // Show any org that has a valid clerk_org_id starting with 'org_' (actual Clerk orgs)
              // This bypasses the membership check since that's not working
              if (org.clerk_org_id && org.clerk_org_id.startsWith('org_')) {
                console.log('[MenuBar] Including org:', org.name, 'clerk_org_id:', org.clerk_org_id);
                return true;
              }
              
              return false;
            });
            console.log('[MenuBar] Filtered orgs:', filtered?.map(o => o.name));
          }
        }}>
          <DropdownMenuTrigger asChild>
            <button className="p-1 hover:bg-white/10 rounded-md transition-colors duration-150 group">
              <img 
                src={logoWhite} 
                alt="Open Insurance" 
                className="h-[18px] w-auto object-contain opacity-95 group-hover:opacity-100 transition-opacity"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            className={getDropdownClassName("backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] rounded-xl p-1 min-w-[220px] font-satoshi text-sm z-[10001]")}
            style={{
              background: getDropdownStyle().background,
              borderColor: getDropdownStyle().borderColor,
              border: `1px solid ${getDropdownStyle().borderColor}`
            }}
            sideOffset={8}
            alignOffset={12}
            align="start"
          >
            <DropdownMenuItem 
              onClick={() => menuActionHandler.handleMenuAction('about-open-insurance-os')}
              className="text-white/95 hover:bg-amber-800/30 hover:text-white transition-all duration-100 rounded-md px-3 py-1.5 font-medium cursor-default text-sm"
            >
              About Open Insurance
            </DropdownMenuItem>
            
            <div className={`h-px ${isNightTime ? 'bg-slate-600/40' : 'bg-white/60'} my-1`} />
            
            <DropdownMenuItem 
              onClick={() => menuActionHandler.handleMenuAction('os-settings')}
              className="text-white/95 hover:bg-amber-800/30 hover:text-white transition-all duration-100 rounded-md px-3 py-1.5 font-medium cursor-default flex justify-between text-sm"
            >
              OS Settings...
              <DropdownMenuShortcut className="text-white/60 font-mono text-xs">⌘;</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => menuActionHandler.handleMenuAction('account-settings')}
              className="text-white/95 hover:bg-amber-800/30 hover:text-white transition-all duration-100 rounded-md px-3 py-1.5 font-medium cursor-default flex justify-between text-sm"
            >
              Account Settings...
              <DropdownMenuShortcut className="text-white/60 font-mono text-xs">⌘,</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => menuActionHandler.handleMenuAction('billing-subscription')}
              className="text-white/95 hover:bg-amber-800/30 hover:text-white transition-all duration-100 rounded-md px-3 py-1.5 font-medium cursor-default text-sm"
            >
              Billing & Subscription
            </DropdownMenuItem>
            
            <div className={`h-px ${isNightTime ? 'bg-slate-600/40' : 'bg-white/60'} my-1`} />
            
            <DropdownMenuItem 
              onClick={() => menuActionHandler.handleMenuAction('notification-preferences')}
              className="text-white/95 hover:bg-amber-800/30 hover:text-white transition-all duration-100 rounded-md px-3 py-1.5 font-medium cursor-default text-sm"
            >
              Notification Preferences
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => menuActionHandler.handleMenuAction('security-settings')}
              className="text-white/95 hover:bg-amber-800/30 hover:text-white transition-all duration-100 rounded-md px-3 py-1.5 font-medium cursor-default text-sm"
            >
              Security Settings
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => window.dispatchEvent(new CustomEvent('changePinAction'))}
              className="text-white/95 hover:bg-amber-800/30 hover:text-white transition-all duration-100 rounded-md px-3 py-1.5 font-medium cursor-default text-sm"
            >
              Change PIN
            </DropdownMenuItem>
            
            <div className={`h-px ${isNightTime ? 'bg-slate-600/40' : 'bg-white/60'} my-1`} />
            
            <div 
              className="text-white/60 px-3 py-1.5 text-xs uppercase tracking-wide cursor-default"
            >
              Switch Organization
            </div>
            {organizations
              ?.filter(org => {
                // Only show valid orgs
                if (!org || !org.id || !org.name) return false;
                
                // ONLY show orgs with valid Clerk org IDs (starts with 'org_')
                // No more Personal workspace concept - users should use actual Clerk organizations
                if (org.clerk_org_id && org.clerk_org_id.startsWith('org_')) {
                  return true;
                }
                
                return false;
              })
              .map((org) => (
              <DropdownMenuItem 
                key={org.id}
                onClick={() => {
                  // If clicking on current org, do nothing (just close dropdown)
                  if (currentOrganization?.id === org.id) {
                    return;
                  }
                  // Show confirmation dialog for switching to different org
                  setOrgSwitchDialog({
                    isOpen: true,
                    targetOrg: { id: org.id, name: org.name }
                  });
                }}
                className="text-white/95 hover:bg-amber-800/30 hover:text-white transition-all duration-100 rounded-md px-3 py-1.5 font-medium cursor-pointer text-sm flex items-center justify-between"
              >
                <span>{org.name}</span>
                {currentOrganization?.id === org.id && (
                  <span className="text-white/70 text-xs">✓ Active</span>
                )}
              </DropdownMenuItem>
            ))}
            
            <div className={`h-px ${isNightTime ? 'bg-slate-600/40' : 'bg-white/60'} my-1`} />
            
            <DropdownMenuItem
              onClick={() => {
                // Open Clerk's native manager for org admin tasks
                if (orgSwitcherRef.current) {
                  const switcherButton = orgSwitcherRef.current.querySelector('button');
                  if (switcherButton) {
                    switcherButton.click();
                  }
                }
              }}
              className="text-white/95 hover:bg-amber-800/30 hover:text-white transition-all duration-100 rounded-md px-3 py-1.5 font-medium cursor-pointer text-sm"
            >
              Manage organizations...
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => navigate('/lock')}
              className="text-white/95 hover:bg-amber-800/30 hover:text-white transition-all duration-100 rounded-md px-3 py-1.5 font-medium cursor-default flex justify-between text-sm"
            >
              Lock Open Insurance
              <DropdownMenuShortcut className="text-white/60 font-mono text-xs">⌃⌘L</DropdownMenuShortcut>
            </DropdownMenuItem>
            
            <div className={`h-px ${isNightTime ? 'bg-slate-600/40' : 'bg-white/60'} my-1`} />
            
            <DropdownMenuItem 
              onClick={() => menuActionHandler.handleMenuAction('logout')}
              className="text-white/95 hover:bg-amber-800/30 hover:text-white transition-all duration-100 rounded-md px-3 py-1.5 font-medium cursor-default flex justify-between text-sm"
            >
              Log Out
              <DropdownMenuShortcut className="text-white/60 font-mono text-xs">⇧⌘Q</DropdownMenuShortcut>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* macOS-style Menu Bar */}
      <Menubar className="border-none bg-transparent h-full flex-1">
        {/* Quick New Tab Icon - Now Task Creator */}
        <div className="flex items-center mr-2">
          <button 
            onClick={() => handleNewWindow('task')}
            className="p-1.5 hover:bg-white/15 rounded-md transition-colors duration-150 group"
            title="Create New Task"
          >
            <Plus size={14} className="text-foreground/70 group-hover:text-foreground" />
          </button>
        </div>

        {/* Dynamic Menus Based on Active App */}
        {/* File Menu */}
        <MenubarMenu>
          <MenubarTrigger className="h-full px-2 text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 rounded border-none focus:bg-white/10 data-[state=open]:bg-white/10 transition-colors duration-200">
            File
          </MenubarTrigger>
          <MenubarContent 
            className={getDropdownClassName("backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] rounded-xl p-1 font-satoshi text-sm z-[10001]")}
            style={{
              background: getDropdownStyle().background,
              borderColor: getDropdownStyle().borderColor,
              animation: getDropdownStyle().animation,
              border: `1px solid ${getDropdownStyle().borderColor}`
            }}
          >
            <DynamicMenu 
              actions={currentMenuConfig.file || []}
              onActionClick={(actionId) => menuActionHandler.handleMenuAction(actionId, { windowId: activeWindow?.id, appType: activeWindow?.appType })}
              isNightTime={isNightTime}
            />
          </MenubarContent>
        </MenubarMenu>

        {/* Edit Menu */}
        <MenubarMenu>
          <MenubarTrigger className="h-full px-2 text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 rounded border-none focus:bg-white/10 data-[state=open]:bg-white/10 transition-colors duration-200">
            Edit
          </MenubarTrigger>
          <MenubarContent 
            className={getDropdownClassName("backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] rounded-xl p-1 font-satoshi text-sm z-[10001]")}
            style={{
              background: getDropdownStyle().background,
              borderColor: getDropdownStyle().borderColor,
              animation: getDropdownStyle().animation,
              border: `1px solid ${getDropdownStyle().borderColor}`
            }}
          >
            <DynamicMenu 
              actions={currentMenuConfig.edit || []}
              onActionClick={(actionId) => menuActionHandler.handleMenuAction(actionId, { windowId: activeWindow?.id, appType: activeWindow?.appType })}
              isNightTime={isNightTime}
            />
          </MenubarContent>
        </MenubarMenu>

        {/* View Menu */}
        <MenubarMenu>
          <MenubarTrigger className="h-full px-2 text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 rounded border-none focus:bg-white/10 data-[state=open]:bg-white/10 transition-colors duration-200">
            View
          </MenubarTrigger>
          <MenubarContent 
            className={getDropdownClassName("backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] rounded-xl p-1 font-satoshi text-sm z-[10001]")}
            style={{
              background: getDropdownStyle().background,
              borderColor: getDropdownStyle().borderColor,
              animation: getDropdownStyle().animation,
              border: `1px solid ${getDropdownStyle().borderColor}`
            }}
          >
            <DynamicMenu 
              actions={currentMenuConfig.view || []}
              onActionClick={(actionId) => menuActionHandler.handleMenuAction(actionId, { windowId: activeWindow?.id, appType: activeWindow?.appType })}
              isNightTime={isNightTime}
            />
          </MenubarContent>
        </MenubarMenu>

        {/* Window Menu */}
        <MenubarMenu>
          <MenubarTrigger className="h-full px-2 text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 rounded border-none focus:bg-white/10 data-[state=open]:bg-white/10 transition-colors duration-200">
            Window
          </MenubarTrigger>
          <MenubarContent 
            className={getDropdownClassName("backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] rounded-xl p-1 font-satoshi text-sm z-[10001]")}
            style={{
              background: getDropdownStyle().background,
              borderColor: getDropdownStyle().borderColor,
              animation: getDropdownStyle().animation,
              border: `1px solid ${getDropdownStyle().borderColor}`
            }}
          >
            <DynamicMenu 
              actions={currentMenuConfig.window || []}
              onActionClick={(actionId) => menuActionHandler.handleMenuAction(actionId, { windowId: activeWindow?.id, appType: activeWindow?.appType })}
              isNightTime={isNightTime}
            />
          </MenubarContent>
        </MenubarMenu>

        {/* Help Menu */}
        <MenubarMenu>
          <MenubarTrigger className="h-full px-2 text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 rounded border-none focus:bg-white/10 data-[state=open]:bg-white/10 transition-colors duration-200">
            Help
          </MenubarTrigger>
          <MenubarContent 
            className={getDropdownClassName("backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] rounded-xl p-1 font-satoshi text-sm z-[10001]")}
            style={{
              background: getDropdownStyle().background,
              borderColor: getDropdownStyle().borderColor,
              animation: getDropdownStyle().animation,
              border: `1px solid ${getDropdownStyle().borderColor}`
            }}
          >
            <DynamicMenu 
              actions={currentMenuConfig.help || []}
              onActionClick={(actionId) => menuActionHandler.handleMenuAction(actionId, { windowId: activeWindow?.id, appType: activeWindow?.appType })}
              isNightTime={isNightTime}
            />
          </MenubarContent>
        </MenubarMenu>
      </Menubar>

      {/* Status Information - Right Side */}
      <div className="flex items-center gap-4 ml-auto">
        {/* Authentication Status */}
        <AuthStatusIndicator />

        <div className="w-px h-4 bg-white/30" />

        {/* Organization Display & Switcher */}
        {user && (
          <div ref={orgSwitcherRef} className="flex items-center gap-1 text-xs">
            {loading ? (
              <div className="text-white/80 font-medium px-2 py-1 bg-white/10 rounded">Loading org...</div>
            ) : currentOrganization ? (
              <div className="flex items-center gap-2">
                <OrganizationSwitcher
                  appearance={{
                    elements: {
                      rootBox: {
                        display: 'flex',
                        alignItems: 'center'
                      },
                      organizationSwitcherTrigger: {
                        padding: '4px 8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'rgba(255, 255, 255, 0.95) !important',
                        fontSize: '12px',
                        fontWeight: '600',
                        height: '28px',
                        minHeight: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.15)',
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          color: 'rgba(255, 255, 255, 1) !important',
                          transform: 'scale(1.02)'
                        },
                        '&:focus': {
                          background: 'rgba(248, 147, 55, 0.25)',
                          borderColor: 'rgba(248, 147, 55, 0.5)',
                          color: 'rgba(255, 255, 255, 1) !important',
                          outline: 'none',
                          boxShadow: '0 0 0 3px rgba(248, 147, 55, 0.2)'
                        },
                        '&:active': {
                          background: 'rgba(248, 147, 55, 0.3)',
                          transform: 'scale(0.98)'
                        },
                        '&[data-state="open"]': {
                          background: 'rgba(248, 147, 55, 0.25)',
                          borderColor: 'rgba(248, 147, 55, 0.5)',
                          color: 'rgba(255, 255, 255, 1) !important'
                        }
                      },
                      organizationSwitcherTriggerIcon: {
                        width: '14px',
                        height: '14px',
                        color: 'rgba(255, 255, 255, 0.9) !important',
                        opacity: '1 !important'
                      },
                      organizationPreviewAvatarBox: {
                        width: '18px',
                        height: '18px'
                      },
                      organizationPreviewAvatarImage: {
                        width: '18px',
                        height: '18px'
                      },
                      organizationSwitcherPopoverCard: {
                        background: 'rgba(15, 23, 42, 0.98)',
                        backdropFilter: 'blur(24px)',
                        border: '1px solid rgba(248, 147, 55, 0.3)',
                        borderRadius: '12px',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(248, 147, 55, 0.1)',
                        marginTop: '8px'
                      },
                      organizationSwitcherPopoverActionButton: {
                        color: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: 'rgba(255, 255, 255, 1)'
                        }
                      },
                      organizationSwitcherPopoverActionButtonText: {
                        color: 'rgba(255, 255, 255, 0.9)'
                      }
                    }
                  }}
                  hidePersonal={false}
                />
              </div>
            ) : (
              <div className="text-white/80 font-medium text-xs px-2 py-1 bg-red-500/20 border border-red-500/30 rounded">
                No Organization
              </div>
            )}
          </div>
        )}

        <div className="w-px h-4 bg-white/30" />

        {/* Focus Mode Indicator - appears/disappears when active */}
        <FocusModeMenuBarIndicator />

        {/* Notifications */}
        <div className="flex items-center gap-3">
          <NotificationDropdown />
        </div>

        <div className="w-px h-4 bg-white/30" />

        {/* Weather */}
        <button 
          onClick={() => openWidget('weather-widget', 'weather', { x: 100, y: 100 })}
          className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white hover:bg-white/10 rounded px-1.5 py-0.5 transition-colors duration-200 cursor-pointer"
          title="Open Weather Widget"
        >
          <AnimatedWeatherIcon 
            condition={weatherData?.condition || 'clear'} 
            className="w-6 h-6"
            size={24} 
          />
          <WeatherDisplay />
        </button>
        
        <div className="w-px h-4 bg-white/30" />
        
        {/* Time - Clickable Clock */}
        <button 
          onClick={() => openWidget('clock-widget', 'clock', { x: window.innerWidth - 520, y: 70 })}
          className="text-xs text-white/70 hover:text-white hover:bg-white/10 rounded px-2 py-1 transition-colors duration-200 cursor-pointer font-mono tabular-nums"
          title="Open Clock Widget"
        >
          {currentTime}
        </button>
      </div>
      
    </div>
    </>
  );
}

export function OSMenuBar(props: OSMenuBarProps) {
  return <OSMenuBarContent {...props} />;
}